import { matchPath } from "react-router-dom";

import { Routes } from "constants/routes";

export type AddMenuActionId =
  | "addFile"
  | "addFolder"
  | "addForm"
  | "addDocument"
  | "addDeal"
  | "addValue"
  | "addTask"
  | "addPortal"
  | "addUser"
  | "addClient"
  | "addEntity"
  | "addLineItem"
  | "uploadToCloud";

export interface PageMenuConfig {
  contextual: AddMenuActionId[];
  general: AddMenuActionId[];
}

const DEFAULT_CONFIG: PageMenuConfig = {
  contextual: [],
  general: ["addDeal", "addValue", "addTask", "addFile", "addPortal", "uploadToCloud"]
};

export function getMenuConfigForPath(pathname: string): PageMenuConfig {
  // Deal pages (most specific first)
  if (matchPath(pathname, { path: Routes.dealWorkspace, exact: false })) {
    return {
      contextual: ["addFile", "addFolder", "addForm", "addDocument", "uploadToCloud"],
      general: ["addDeal", "addValue", "addTask", "addPortal"]
    };
  }

  if (matchPath(pathname, { path: Routes.dealMonitoring, exact: false })) {
    return {
      contextual: ["addValue"],
      general: ["addDeal", "addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  if (matchPath(pathname, { path: Routes.dealTaskManage })) {
    return {
      contextual: ["addTask"],
      general: ["addDeal", "addValue", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Deal catch-all (Overview, Report, Dashboard, Amortization, Look Through, etc.)
  if (matchPath(pathname, { path: Routes.deal })) {
    return {
      contextual: [],
      general: ["addDeal", "addValue", "addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Data Operations - Files tab
  if (matchPath(pathname, { path: Routes.fileRepository })) {
    return {
      contextual: ["addFile", "uploadToCloud"],
      general: ["addDeal", "addValue", "addTask", "addPortal"]
    };
  }

  // Data Operations - Data tab
  if (matchPath(pathname, { path: Routes.monitoringData })) {
    return {
      contextual: ["addDeal", "addValue"],
      general: ["addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Data Operations - Other tabs
  if (matchPath(pathname, { path: Routes.monitoring, exact: false })) {
    return {
      contextual: [],
      general: ["addDeal", "addValue", "addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Task Management
  if (matchPath(pathname, { path: Routes.taskManagement })) {
    return {
      contextual: ["addTask"],
      general: ["addDeal", "addValue", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Data Rooms
  if (matchPath(pathname, { path: Routes.dataRooms, exact: false })) {
    return {
      contextual: ["addPortal"],
      general: ["addDeal", "addValue", "addFile", "addTask", "uploadToCloud"]
    };
  }

  // Smart Sheet
  if (matchPath(pathname, { path: Routes.quickView })) {
    return {
      contextual: ["addLineItem"],
      general: ["addDeal", "addValue", "addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // User Management - Users tab
  if (matchPath(pathname, { path: Routes.users })) {
    return {
      contextual: ["addUser"],
      general: ["addDeal", "addValue", "addTask", "addPortal", "uploadToCloud"]
    };
  }

  // User Management - Clients tab (list page only, not the client detail route)
  if (matchPath(pathname, { path: Routes.clients, exact: true })) {
    return {
      contextual: ["addClient"],
      general: ["addDeal", "addValue", "addTask", "addPortal", "uploadToCloud"]
    };
  }

  // Client detail — Tasks tab: Add Task scopes to the client.
  if (matchPath(pathname, { path: Routes.clientTasks })) {
    return {
      contextual: ["addTask"],
      general: ["addDeal", "addValue", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Client detail — Entities tab: Add Entity (Admin / Team member).
  if (matchPath(pathname, { path: Routes.clientEntities })) {
    return {
      contextual: ["addEntity"],
      general: ["addDeal", "addValue", "addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  // Client detail catch-all (dashboard / overview / reporting / …) — Add Task available in general.
  if (matchPath(pathname, { path: Routes.client })) {
    return {
      contextual: [],
      general: ["addDeal", "addValue", "addTask", "addFile", "addPortal", "uploadToCloud"]
    };
  }

  return DEFAULT_CONFIG;
}
